
import { useCallback, useState, useRef } from 'react';
import { apiClient } from '@/lib/api/api-client';
import { useOrganizationStore } from '@/store/organization.store';
import { useOrganizationSubscription, invalidateSubscriptionCache } from './useOrganizationSubscription';
import type {
  UpdateSeatsDto,
  SeatAssignmentDto,
  SeatUsageDto,
  SeatChangeHistoryDto,
  OrganizationSubscription
} from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useSubscriptionSeats');

// Types for seat preview
export interface SeatPreview {
  currentSeats: number;
  newSeats: number;
  prorationAmount: number;
  oldMonthlyAmount: number;
  newMonthlyAmount: number;
  effectiveDate: string;
  lineItems: {
    description: string;
    amount: number;
    proration: boolean;
  }[];
  canChange: boolean;
  validationMessage?: string;
}

interface UseSubscriptionSeatsReturn {
  // State
  subscription: OrganizationSubscription | null;
  seatUsage: SeatUsageDto | null;
  seatHistory: SeatChangeHistoryDto[];
  seatPreview: SeatPreview | null;
  isLoading: boolean;
  isUpdating: boolean;
  isPreviewing: boolean;
  error: Error | null;

  // Actions
  updateSeats: (seats: number, immediate?: boolean) => Promise<void>;
  previewSeatChange: (newSeatCount: number) => Promise<SeatPreview | null>;
  clearPreview: () => void;
  assignSeat: (userId: string) => Promise<void>;
  unassignSeat: (userId: string) => Promise<void>;
  fetchSeatUsage: () => Promise<void>;
  fetchSeatHistory: (limit?: number) => Promise<void>;
  recalculateSeatAssignments: () => Promise<{
    totalMembers: number;
    pendingInvites: number;
    expectedSeatsUsed: number;
    actualSeatsUsed: number;
    membersWithoutSeats: number;
    membersAssignedSeats: number;
    summary: string;
  }>;

  // Computed
  canReduceSeats: boolean;
  availableSeats: number;
  usedSeats: number;
  seatUtilization: number;
}

export function useSubscriptionSeats(): UseSubscriptionSeatsReturn {
  const { currentOrganization } = useOrganizationStore();
  const { subscription } = useOrganizationSubscription(currentOrganization?.id);

  const [seatUsage, setSeatUsage] = useState<SeatUsageDto | null>(null);
  const [seatHistory, setSeatHistory] = useState<SeatChangeHistoryDto[]>([]);
  const [seatPreview, setSeatPreview] = useState<SeatPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Debounce ref for preview calls
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch seat usage
  const fetchSeatUsage = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiClient<{ data: SeatUsageDto }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats`
      );
      
      setSeatUsage(response.data);
    } catch (err) {
      logger.error('Failed to fetch seat usage:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization?.id]);

  // Fetch seat history
  const fetchSeatHistory = useCallback(async (limit = 50) => {
    if (!currentOrganization?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiClient<{ data: SeatChangeHistoryDto[] }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats/history?limit=${limit}`
      );
      
      setSeatHistory(response.data || []);
    } catch (err) {
      logger.error('Failed to fetch seat history:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization?.id]);

  // Preview seat change - calls API to get exact proration from Stripe
  const previewSeatChange = useCallback(async (newSeatCount: number): Promise<SeatPreview | null> => {
    if (!currentOrganization?.id) return null;

    // Clear any pending preview timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    // Don't preview if same as current seats
    if (subscription?.seats === newSeatCount) {
      setSeatPreview(null);
      return null;
    }

    try {
      setIsPreviewing(true);
      setError(null);

      const response = await apiClient<{ data: SeatPreview; message: string }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats/preview`,
        {
          method: 'POST',
          body: { seatCount: newSeatCount },
        }
      );

      const preview = response.data;
      setSeatPreview(preview);
      return preview;
    } catch (err) {
      logger.error('Failed to preview seat change:', err);
      // Don't set error for preview failures - just clear preview
      setSeatPreview(null);
      return null;
    } finally {
      setIsPreviewing(false);
    }
  }, [currentOrganization?.id, subscription?.seats]);

  // Clear preview
  const clearPreview = useCallback(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    setSeatPreview(null);
  }, []);

  // Update seat count
  const updateSeats = useCallback(async (seats: number, immediate = true) => {
    if (!currentOrganization?.id || !subscription) {
      throw new Error('No active subscription');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const dto: UpdateSeatsDto = { seats, immediate };
      
      await apiClient<{ data: OrganizationSubscription }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats`,
        {
          method: 'PUT',
          body: dto,
        }
      );

      // Invalidate cache globally to trigger updates in all subscription hook instances
      invalidateSubscriptionCache(currentOrganization.id);

      // Refresh local data
      await Promise.all([
        fetchSeatUsage(),
        fetchSeatHistory(),
      ]);
    } catch (err) {
      logger.error('Failed to update seats:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [currentOrganization?.id, subscription, fetchSeatUsage, fetchSeatHistory]);

  // Assign a seat to a user
  const assignSeat = useCallback(async (userId: string) => {
    if (!currentOrganization?.id) {
      throw new Error('No organization selected');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const dto: SeatAssignmentDto = { userId, active: true };
      
      const response = await apiClient<{ data: SeatUsageDto }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats/assign`,
        {
          method: 'POST',
          body: dto,
        }
      );

      setSeatUsage(response.data);
    } catch (err) {
      logger.error('Failed to assign seat:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [currentOrganization?.id]);

  // Unassign a seat from a user
  const unassignSeat = useCallback(async (userId: string) => {
    if (!currentOrganization?.id) {
      throw new Error('No organization selected');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const dto: SeatAssignmentDto = { userId, active: false };
      
      const response = await apiClient<{ data: SeatUsageDto }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats/assign`,
        {
          method: 'POST',
          body: dto,
        }
      );

      setSeatUsage(response.data);
    } catch (err) {
      logger.error('Failed to unassign seat:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [currentOrganization?.id]);

  // Recalculate seat assignments
  const recalculateSeatAssignments = useCallback(async () => {
    if (!currentOrganization?.id) {
      throw new Error('No organization selected');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await apiClient<{ 
        data: {
          totalMembers: number;
          pendingInvites: number;
          expectedSeatsUsed: number;
          actualSeatsUsed: number;
          membersWithoutSeats: number;
          membersAssignedSeats: number;
          summary: string;
        }
      }>(
        `/subscriptions/organizations/${currentOrganization.id}/seats/recalculate`,
        {
          method: 'POST',
        }
      );
      
      // Refresh seat usage data after recalculation
      await fetchSeatUsage();
      
      // Return the response for the component to handle
      return response.data;
    } catch (err) {
      logger.error('Failed to recalculate seat assignments:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [currentOrganization?.id, fetchSeatUsage]);

  // Computed values
  const canReduceSeats = subscription?.allowSeatReduction ?? true;
  const availableSeats = seatUsage?.availableSeats ?? 0;
  const usedSeats = seatUsage?.usedSeats ?? 0;
  const seatUtilization = seatUsage?.totalSeats 
    ? (seatUsage.usedSeats / seatUsage.totalSeats) * 100 
    : 0;

  return {
    // State
    subscription,
    seatUsage,
    seatHistory,
    seatPreview,
    isLoading,
    isUpdating,
    isPreviewing,
    error,

    // Actions
    updateSeats,
    previewSeatChange,
    clearPreview,
    assignSeat,
    unassignSeat,
    fetchSeatUsage,
    fetchSeatHistory,
    recalculateSeatAssignments,
    
    // Computed
    canReduceSeats,
    availableSeats,
    usedSeats,
    seatUtilization,
  };
} 